// Copy of DispenseRxData from dispenseRxData.js for the Picked Patients page only,
// so changes here never affect the other delivery screens. Differences:
// renderRxFooter (extra content inside each RX card; it has its own dose
// choice, so the delivery dose boxes are not shown) and no
// Unconsumed / Refused Dose section.
import React, { useState, useEffect, useCallback } from 'react'
import { useCustomDispatch } from '../../hooks'
import { useAuthSelector } from '../../services/selectors/authSelector'

import { UploadTripilicatesPatientSigns } from './uploadTripilicatesPatientSigns'

import { useLocation } from 'react-router-dom'
import { NewRxesConsultationDropdown } from './deliveryConsultationDropdown'
import { FlexiProofUpload } from './flexiProofUpload'
import { CopayPaymentStatus } from './copayPaymentStatus'
import { removeVitalPatientById } from '../../services/slices/dispense/vitalPatients'

import VitalSignsEntry from './vitalSignsEntry'
import { sanitizeHtml } from '../../utils/helper/security';
import { getDefaultUnconsumedDoseEntry, getUnconsumedDoseQuantityError, getUnconsumedDoseRxKey } from '../../utils/helper/unconsumedDose';
import { getDefaultDeliverRxIds, isRxFullyDelivered, isRxSelectableForDelivery, mergeDeliveredDosesForDisplay } from '../../utils/helper/deliveredRxDisplay';

import { GenrateRxNumberDataPDF, InvestageRxPDF, ReturnedPatches } from './dispenseRxData'

// The driver only checks and comments on this page; no unconsumed dose entry.
const SHOW_UNCONSUMED_DOSE = false
// Nor the other delivery steps: consultation, patch returns, triplicates,
// investigate PDF, copay and flexi proof. Only the RX itself is shown.
const SHOW_DELIVERY_EXTRAS = false

export const PickedPatientsRxData = ({ rxData,
	newRxCounsellingValues,
	setNewRxCounsellingValues,
	type = '',
	patientData,
	selectedDoses,
	setSelectedDoses,
	hideFlexi = true,
	flexiProofImages = {},
	setFlexiProofImages = () => { },
	setCopayPaymentOptions = () => { },
	setOpenIndex = () => { },
	showAddVitalButton = false,
	selectedDeliverRxIds = [],
	setSelectedDeliverRxIds = () => { },
	isActiveDeliverRxCard = true,
	deliverRxSelectionEnabled = true,
	unconsumedDoseEntries = {},
	setUnconsumedDoseEntries = () => { },
	unconsumedDoseErrors = {},
	setUnconsumedDoseErrors = () => { },
	// Extra content inside each RX card, under the doses.
	renderRxFooter = null,
}) => {

	const [image, setImage] = useState('')
	// Blister Pack dropdown (Flexi tab): which packs are opened. Closed by default.
	const [openBlisterPacks, setOpenBlisterPacks] = useState({})
	const location = useLocation()?.pathname

	const dispatch = useCustomDispatch()
	const { driverID,
		//  hcpUser 
	} = useAuthSelector()

	// Handle copay payment status changes - memoized to prevent infinite loops
	const handleCopayPaymentChange = useCallback((winrxDataId, paymentData) => {
		if (setCopayPaymentOptions) {
			if (paymentData) {
				setCopayPaymentOptions(prev => ({
					...prev,
					[winrxDataId]: paymentData
				}));
			} else {
				setCopayPaymentOptions(prev => {
					const updated = { ...prev };
					delete updated[winrxDataId];
					return updated;
				});
			}
		}
	}, [setCopayPaymentOptions]);

	const allowedPages = ['/e-sign',
		'/flexi-delivery',
		'/driver-today-pending',
		'/add-vitals',
		'/driver-route-today',
		'/driver-today-missed-route',
		'/driver-missed-route'];

	const isAllowedPage = allowedPages.includes(location);
	// Only show deliver RX selection (checkboxes, etc.) on these pages
	const showDeliverRxSelection = isAllowedPage;

	// const isAddVitalsPage = showAddVitalButton ? true : hcpUser === 0 && location === '/add-vitals'
	const isAddVitalsPage = showAddVitalButton

	// Handle vitals success - remove patient from list
	const handleVitalsSuccess = useCallback((vitalsData) => {
		if (vitalsData?.patientId && isAddVitalsPage) {
			dispatch(removeVitalPatientById(vitalsData.patientId))
			setOpenIndex(null)
		}
	}, [isAddVitalsPage, dispatch, setOpenIndex])

	// Default-select only actionable RXs. Fully delivered and handed-over rows
	// remain visible as history but never enter the delivery selection.
	const rxDataKey = rxData?.length ? rxData.map((r) => r?.id).filter(Boolean).join(',') : ''
	useEffect(() => {
		if (!showDeliverRxSelection || !isActiveDeliverRxCard || !rxDataKey || !setSelectedDeliverRxIds) return
		setSelectedDeliverRxIds(getDefaultDeliverRxIds(rxData))
	}, [showDeliverRxSelection, isActiveDeliverRxCard, rxDataKey, setSelectedDeliverRxIds, rxData])

	// Blister pack (Flexi tab only): the drugs the blister sync marked Flexi (is_blister_flexi)
	// for this patient and the same route day go out together in one pack, so they are shown
	// together with one combined Collect amount and one proof image. Every other drug renders
	// exactly as before.
	const blisterPackKeyOf = (rx) => (
		location === '/flexi-delivery'
		&& rx?.is_blister_flexi === true
		&& Boolean(rx?.flexi_type)
		&& rx?.delivery_status === 'pending'
	) ? `blister-${patientData?.id ?? ''}-${rx?.flexi_delivery_date ?? ''}` : null

	// Keep each pack's drugs next to each other, at the place of its first drug.
	const groupBlisterPacks = (list = []) => {
		const placed = {}
		const ordered = list.reduce((acc, rx) => {
			const key = blisterPackKeyOf(rx)
			if (!key) return [...acc, rx]
			if (placed[key]) return acc
			placed[key] = true
			return [...acc, ...list.filter((other) => blisterPackKeyOf(other) === key)]
		}, [])
		// A patient with a pack: the pack(s) first, then the other drugs together in their own box.
		if (!ordered.some((rx) => blisterPackKeyOf(rx))) return ordered
		return [...ordered.filter((rx) => blisterPackKeyOf(rx)), ...ordered.filter((rx) => !blisterPackKeyOf(rx))]
	}

	// Ticked for delivery.
	const isRxTicked = (rx) => selectedDeliverRxIds?.includes(rx?.id)

	// The tick box on the Blister Pack heading ticks or unticks every drug in the pack together.
	const handleBlisterPackToggle = (packRxs) => {
		if (!setSelectedDeliverRxIds || !deliverRxSelectionEnabled) return
		const packIds = packRxs.filter(isRxSelectableForDelivery).map((rx) => rx?.id).filter((id) => id != null)
		const allTicked = packIds.length > 0 && packIds.every((id) => selectedDeliverRxIds?.includes(id))
		setSelectedDeliverRxIds((prev) => {
			const rest = (prev || []).filter((id) => !packIds.includes(id))
			return allTicked ? rest : [...rest, ...packIds]
		})
	}

	// One YES/NO for the pack, saved on every drug in it that has a copay (per drug, as before).
	const handleBlisterPackPaymentChange = (packRxs, paymentData) => {
		packRxs.forEach((rx) => {
			if (rx?.id == null || !(parseFloat(rx?.copay) > 0)) return
			handleCopayPaymentChange(rx?.id, paymentData ? { ...paymentData, winrx_data_id: rx?.id } : null)
		})
	}

	// One proof image for the pack, sent for every drug in it (the API stores proof per drug).
	const setBlisterPackProofImage = (packRxs, file) => {
		setFlexiProofImages((prev) => {
			const updated = { ...prev }
			packRxs.forEach((rx) => {
				if (rx?.id == null) return
				if (file === null) delete updated[rx?.rxnumber]
				else updated[rx?.rxnumber] = { id: rx?.id, file }
			})
			return updated
		})
	}

	// Put each row back together: a pack's heading, then its drug cards, then its footer.
	const assembleBlisterRows = (items) => {
		const list = items || []
		const out = []
		for (let i = 0; i < list.length; i++) {
			const item = list[i]
			if (!item.packKey) {
				out.push(<React.Fragment key={item.key}>{item.before}{item.row}{item.after}</React.Fragment>)
				continue
			}
			let j = i
			while (j + 1 < list.length && list[j + 1].packKey === item.packKey) j++
			const pack = list.slice(i, j + 1)
			out.push(
				<React.Fragment key={`pack-${item.key}`}>
					{item.before}
					<div>
						{pack.map((p) => <React.Fragment key={p.key}>{p.row}</React.Fragment>)}
					</div>
					{pack.map((p) => <React.Fragment key={`after-${p.key}`}>{p.after}</React.Fragment>)}
				</React.Fragment>
			)
			i = j
		}
		return out
	}

	return (
		<React.Fragment>
			{/* {patientData?.add_vital ? null : 
			(hcpUser === 0 && isAllowedPage && patientData?.id && 
				(patientData?.is_vital === 1 && ((driverID === patientData?.driver_hcp_id) || !patientData?.driver_hcp_id))) && (
				<VitalSignsEntry
					patientId={patientData?.id}
					onSuccess={handleVitalsSuccess}
					// onRemovePatient={(patientId) => dispatch(removeVitalPatientById(patientId))}
					isAddVitalsPage={isAddVitalsPage}
				/>
			)} */}

			{assembleBlisterRows(groupBlisterPacks(rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				}))
				?.map((value, index, list) => {
					const doseHistory = value?.investigate_dilaudid_delivery_history || value?.investigate_dilaudid_delivery_all
					const dilaudid_delivery =
						location === '/driver-today-missed-route' ? mergeDeliveredDosesForDisplay(value?.investigate_dilaudid_today_missed, doseHistory) :
							location === '/driver-missed-route' ? mergeDeliveredDosesForDisplay(value?.investigate_dilaudid_final_missed, doseHistory) :
								doseHistory

					const isEmergency = value?.rxsig ? value?.rxsig.toLowerCase().includes('emergency') : false;
					const isFlexiDelivery = value?.flexi_delivery
					const isFlexiDeliveryDate = value?.flexi_date
					const flexiType = value?.flexi_type
					const staleStatusColor = value?.flexi_stale_status === 'orange' ? '#ff4d01' : value?.flexi_stale_status === 'red' ? 'red' : '#31C48D'
					const showStaleStatus = !value?.flexi_expiry_date || Boolean(value?.flexi_stale_status)
					const blisterPackKey = blisterPackKeyOf(value)
					const isBlisterPackStart = Boolean(blisterPackKey) && blisterPackKeyOf(list[index - 1]) !== blisterPackKey
					// Other (non-blister) drugs of a patient with a pack: framed together in one box,
					// each drug's own layout unchanged.
					const isOtherRx = !blisterPackKey && list.some((rx) => blisterPackKeyOf(rx))
					const isOtherStart = isOtherRx && (index === 0 || Boolean(blisterPackKeyOf(list[index - 1])))
					const isOtherEnd = isOtherRx && index === list.length - 1
					const otherCount = isOtherStart ? list.filter((rx) => !blisterPackKeyOf(rx)).length : 0
					const isBlisterPackEnd = Boolean(blisterPackKey) && blisterPackKeyOf(list[index + 1]) !== blisterPackKey
					const blisterPackRxs = blisterPackKey ? list.filter((rx) => blisterPackKeyOf(rx) === blisterPackKey) : []
					const blisterPackSelectable = blisterPackRxs.filter((rx) => rx?.id != null && isRxSelectableForDelivery(rx))
					const blisterPackTicked = blisterPackSelectable.length > 0 && blisterPackSelectable.every(isRxTicked)
					// A patient with a pack: one Collect (all ticked drugs' copays) and one proof image,
					// after the last drug, for the pack and the other drugs together.
					const isCombinedFooter = index === list.length - 1 && list.some((rx) => blisterPackKeyOf(rx))
					const combinedRxs = isCombinedFooter ? list.filter((rx) => rx?.flexi_type && rx?.delivery_status === 'pending') : []
					const combinedCopay = combinedRxs.filter(isRxTicked).reduce((sum, rx) => sum + (parseFloat(rx?.copay) || 0), 0)
					const hasDeliveredParentStatus = String(value?.delivery_status || '').toLowerCase() === 'delivered'
					const isFullyDelivered = isRxFullyDelivered(value)
					const rxCanDeliver = value?.rx_access?.can_deliver !== false
					const assignedOwnerName = value?.assigned_rx_owner?.name
					// can_deliver logic only when status is not delivered: only when ALL doses have can_deliver === false → row disabled
					const allDosesCanDeliverFalse = !hasDeliveredParentStatus && dilaudid_delivery?.length > 0 &&
						dilaudid_delivery.every((d) => d?.can_deliver === false)
					const isParentChecked = showDeliverRxSelection && !isFullyDelivered && selectedDeliverRxIds?.includes(value?.id)
					const isCheckboxDisabled = showDeliverRxSelection && (!deliverRxSelectionEnabled || isFullyDelivered || !rxCanDeliver || allDosesCanDeliverFalse)
					const rxKey = getUnconsumedDoseRxKey(value)
					const unconsumedEntry = unconsumedDoseEntries?.[rxKey] || getDefaultUnconsumedDoseEntry()
					const unconsumedEntryErrors = unconsumedDoseErrors?.[rxKey] || {}
					const unconsumedDisabled = showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)

					const handleDeliverRxToggle = () => {
						if (value?.id == null || !setSelectedDeliverRxIds || !deliverRxSelectionEnabled || isFullyDelivered || !rxCanDeliver) return
						if (allDosesCanDeliverFalse) return
						const willBeChecked = !selectedDeliverRxIds?.includes(value.id)
						setSelectedDeliverRxIds(prev => {
							const next = prev ? [...prev] : []
							const idx = next.indexOf(value.id)
							if (idx === -1) next.push(value.id)
							else next.splice(idx, 1)
							return next
						})
						if (willBeChecked && dilaudid_delivery?.length > 0) {
							// Select all doses that have can_deliver === true (same as DoseFrequencySelector default)
							const deliverableDoses = dilaudid_delivery
								.filter((d) => d?.can_deliver === true)
								.map((d) => ({ frequency: d?.frequency, dose_id: d?.id, dose_no: d?.dose_no, dose_gap_prompt: d?.dose_gap_prompt }))
							setSelectedDoses(prev => ({
								...prev,
								[value.rxnumber]: deliverableDoses.length > 0 ? deliverableDoses : [],
							}))
						} else if (!willBeChecked) {
							setSelectedDoses(prev => ({ ...prev, [value.rxnumber]: [] }))
						}
					}

					const handleUnconsumedToggle = (enabled) => {
						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: enabled ? {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
							} : getDefaultUnconsumedDoseEntry(),
						}))

						setUnconsumedDoseErrors((prevState) => {
							if (!prevState?.[rxKey]) return prevState
							const nextState = { ...prevState }
							delete nextState[rxKey]
							return nextState
						})
					}

					const handleUnconsumedQuantityChange = (inputValue) => {
						const quantityError = getUnconsumedDoseQuantityError({
							rx: value,
							selectedDoses,
							quantity: inputValue,
						})

						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
								quantity: inputValue,
							},
						}))

						setUnconsumedDoseErrors((prevState) => ({
							...prevState,
							[rxKey]: {
								...(prevState?.[rxKey] || {}),
								quantity: quantityError,
							},
						}))
					}

					const handleUnconsumedSafetyStatusChange = (safetyStatus) => {
						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
								safetyStatus,
							},
						}))

						setUnconsumedDoseErrors((prevState) => ({
							...prevState,
							[rxKey]: {
								...(prevState?.[rxKey] || {}),
								safetyStatus: '',
							},
						}))
					}

					// Split into heading / drug card / footer, so a pack's cards can share one scroll box.
					return {
						key: index,
						packKey: blisterPackKey,
						before: (<React.Fragment>
							{isBlisterPackStart && (
								<div
									className="mt-3 flex cursor-pointer select-none items-center gap-2 rounded-t-md border border-b-0 border-teal-600 bg-teal-50 px-3 py-2"
									role="button"
									aria-expanded={Boolean(openBlisterPacks[blisterPackKey])}
									onClick={() => setOpenBlisterPacks((prev) => ({ ...prev, [blisterPackKey]: !prev[blisterPackKey] }))}
								>
									{showDeliverRxSelection && (
										<label
											className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${!deliverRxSelectionEnabled ? 'opacity-50 bg-gray-100 text-gray-500 cursor-not-allowed border-gray-100' : blisterPackTicked ? 'cursor-pointer border-blue-600 bg-blue-600 hover:bg-blue-700 text-white' : 'cursor-pointer border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleBlisterPackToggle(blisterPackRxs); }}
										>
											<input
												type="checkbox"
												checked={blisterPackTicked}
												onChange={(e) => { e.stopPropagation(); handleBlisterPackToggle(blisterPackRxs); }}
												disabled={!deliverRxSelectionEnabled}
												className="sr-only"
												tabIndex={-1}
											/>
											{blisterPackTicked && (
												<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
													<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
												</svg>
											)}
										</label>
									)}
									{/* Same shape as the "Show Instructions" button (rounded, 2px border), at its own size. */}
									<div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-teal-600 bg-white py-1 px-2.5 text-xs font-semibold text-teal-700">
										<svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
											<rect x="1.5" y="1.5" width="13" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
											<circle cx="5.5" cy="5.5" r="1.6" fill="currentColor" />
											<circle cx="10.5" cy="5.5" r="1.6" fill="currentColor" />
											<circle cx="5.5" cy="10.5" r="1.6" fill="currentColor" />
											<circle cx="10.5" cy="10.5" r="1.6" fill="currentColor" />
										</svg>
										Blister Pack
										<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[11px] font-semibold">
											{blisterPackRxs.length}
										</span>
									</div>
									<svg className={`ml-auto h-5 w-5 shrink-0 text-teal-700 transition-transform duration-200 ${openBlisterPacks[blisterPackKey] ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
										<path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
									</svg>
								</div>
							)}
							{isOtherStart && (
								<div className="mt-3 flex items-center rounded-t-md border border-b-0 border-slate-300 bg-slate-50 px-3 py-2">
									<div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-slate-400 bg-white py-1 px-2.5 text-xs font-semibold text-slate-700">
										Regular Medications
										<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-500 text-white text-[11px] font-semibold">
											{otherCount}
										</span>
									</div>
								</div>
							)}
						</React.Fragment>),
						row: (
							<div className={`${blisterPackKey ? `border-x border-teal-600 bg-teal-50 px-2 py-1 ${openBlisterPacks[blisterPackKey] ? '' : 'hidden'}` : isOtherRx ? `border-x border-slate-300 bg-slate-50 px-2 py-1 ${isOtherEnd ? 'mb-3 rounded-b-md border-b pb-2' : ''}` : 'mt-2 mb-2'} break-all ${allDosesCanDeliverFalse ? 'opacity-60' : ''}`}>
								<div
									className="rounded-md border border-gray-200 bg-white p-2"
									style={value?.is_din_matched?.status === 1 ? { backgroundColor: value?.is_din_matched?.color || 'white' } : undefined}
								>

									<div className='flex gap-2 justify-between items-center flex-wrap'>
										{/* flex-wrap: the row can now carry the Rx number, "Delivered"
										    AND "Witness Required" at once (tickets 2026-08-07-001 stopped
										    hiding completed rows). Without wrapping the badges overflow
										    and paint on top of the Rx number on a phone-width screen. */}
										<div className='flex gap-2 items-center flex-wrap'>
											{showDeliverRxSelection && !blisterPackKey && (
												<label
													className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${isCheckboxDisabled ? 'opacity-50 bg-gray-100 text-gray-500 cursor-not-allowed border-gray-100' : isParentChecked ? 'cursor-pointer border-blue-600 bg-blue-600 hover:bg-blue-700 text-white' : 'cursor-pointer border-gray-100 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
													onClick={(e) => {
														if (isCheckboxDisabled) {
															e.preventDefault();
															return;
														}
														e.preventDefault();
														handleDeliverRxToggle();
													}}
												>
													<input
														type="checkbox"
														checked={!!isParentChecked}
														onChange={(e) => { e.stopPropagation(); handleDeliverRxToggle(); }}
														disabled={isCheckboxDisabled}
														className="sr-only"
														tabIndex={-1}
													/>
													{isParentChecked && (
														<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
													)}
												</label>
											)}
											{/* whitespace-nowrap on every badge: the row wrapper carries
											    `break-all` for the long SIG text, which was also breaking
											    these short labels mid-word -- "Deliver ed", "Witness Requ ired". */}
											<p className="text-sm font-normal text-white rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>
												{value?.rxnumber}
											</p>

											{isFullyDelivered && (
												<p className="text-sm font-normal text-white rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: '#0F766E', paddingLeft: '10px', paddingRight: '10px' }}>
													Delivered
												</p>
											)}

											{value.rx_witness === 'Y' &&
												<p className="text-sm font-normal text-white bg-yellow-500 rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: '#6A057B', paddingLeft: '10px', paddingRight: '10px' }}>
													Witness Required
												</p>}

											<GenrateRxNumberDataPDF value={value} />

											{SHOW_DELIVERY_EXTRAS && value?.is_triplicates_pending?.is_triplicates &&
												<div className='text-sm font-normal text-white bg-red-500 rounded-md' onClick={() => setImage(value?.is_triplicates_pending?.precription_image)}>
													<UploadTripilicatesPatientSigns
														index={index}
														rxData={value}
														image={image} />
												</div>
										}
									</div>

									{
											isFlexiDelivery && !blisterPackKey &&
											<div className='flex gap-1 flex-wrap'>
												{hideFlexi &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														FLEXI
													</div>}

												{
													!hideFlexi && isFlexiDeliveryDate &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{isFlexiDeliveryDate}
													</div>
												}

												{flexiType && (
													<div style={{ height: '25px' }} className={`text-xs font-normal ${flexiType === 'delivery' ? 'bg-blue-500' : 'bg-purple-500'} text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{flexiType.toUpperCase()}
													</div>
												)}

												{showStaleStatus && (
													<div style={{ height: '25px', background: staleStatusColor }} className={`text-xs font-normal text-white  px-4 py-1 flex items-center h-fit justify-center rounded-full`}>
														STALE
													</div>
												)}
											</div>
										}
									</div>

									<h3 className="text-sm font-medium my-1">
										[{value.refill_reqty}] {value.drug}
									</h3>

									<p className="text-sm text-gray-900">{value?.rxsig}</p>

									<p className='pt-1' dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.prescriptionExpiryDay) }} />
									{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}

									{SHOW_DELIVERY_EXTRAS && patientData?.delivery_status !== 'delivered' && value?.is_din_matched?.is_fentanyl === 1
										&& patientData?.patients_win_rx_data?.length > 0 &&
										<div className={showDeliverRxSelection && patientData && !selectedDeliverRxIds?.includes(value?.id) ? 'opacity-50 pointer-events-none' : ''}>
											<ReturnedPatches
												index={index}
												rxData={value}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>}
									{SHOW_DELIVERY_EXTRAS && <InvestageRxPDF value={value} />}

									{SHOW_DELIVERY_EXTRAS && type === 'pending_counselling_rxes' &&
										<div>
											{value?.drug_counselling_sheet &&
												<div className='mt-2 border border-gray-400 rounded-md p-2 text-sm h-80  bg-white overflow-y-auto pending-counselling-sheet'
													dangerouslySetInnerHTML={{ __html: sanitizeHtml(value?.drug_counselling_sheet) }}
												>
												</div>
											}
											<div className='mt-2'>
												<NewRxesConsultationDropdown
													type={type}
													counsellingValues={newRxCounsellingValues}
													rxData={value}
													setCounsellingValues={setNewRxCounsellingValues}
													disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)} />
											</div>
										</div>
									}

									{ !SHOW_DELIVERY_EXTRAS || value?.counsling_provided === 1 ? null :
										value?.is_new_rx && type === 'new_rx' && !isEmergency && (patientData ? patientData?.delivery_status !== 'delivered' : value?.delivery_status !== 'delivered') &&
										<div className='mt-2'>
											<NewRxesConsultationDropdown
												type={type}
												counsellingValues={newRxCounsellingValues}
												rxData={value}
												setCounsellingValues={setNewRxCounsellingValues}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>
									}


									{typeof renderRxFooter === 'function' ? renderRxFooter(value) : null}

									{SHOW_UNCONSUMED_DOSE && String(value?.delivery_status || '').toLowerCase() !== 'delivered' && !blisterPackKey && (
										<div className={`mt-1 rounded-lg border border-gray-200 bg-white p-1 ${unconsumedDisabled ? 'opacity-60' : ''}`}>
											<div className="flex items-center justify-between gap-3" 
													onClick={() => handleUnconsumedToggle(!Boolean(unconsumedEntry?.enabled))}
											
											>
												<div className="min-w-0">
													<p className="text-sm font-medium text-gray-900">Unconsumed / Refused Dose</p>
												</div>
												<button
													type="button"
													disabled={unconsumedDisabled}
													aria-expanded={Boolean(unconsumedEntry?.enabled)}
													aria-label={Boolean(unconsumedEntry?.enabled) ? 'Hide unconsumed or refused dose details' : 'Show unconsumed or refused dose details'}
													onClick={() => handleUnconsumedToggle(!Boolean(unconsumedEntry?.enabled))}
													// className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-700 transition ${unconsumedDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:border-primary hover:text-primary'}`}
												>
													<svg
														className={`h-4 w-4 transition-transform ${unconsumedEntry?.enabled ? 'rotate-90' : ''}`}
														viewBox="0 0 20 20"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
														aria-hidden="true"
													>
														<path
															d="M7.5 5.83325L11.6667 9.99992L7.5 14.1666"
															stroke="currentColor"
															strokeWidth="1.8"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</button>
											</div>

											{unconsumedEntry?.enabled && (
												<div className="mt-2 space-y-3">
													<div className="grid grid-cols-[80px_1fr] gap-0.5 items-start">
														<input
															type="number"
															min="0"
															step="any"
															inputMode="decimal"
															value={unconsumedEntry?.quantity ?? ''}
															onChange={(event) => handleUnconsumedQuantityChange(event.target.value)}
															placeholder="Qty"
															className={`block w-full h-9 rounded-lg border bg-white px-2 text-xs text-gray-900 outline-0 ${unconsumedEntryErrors?.quantity ? 'border-red-500' : 'border-gray-300'} focus:border-primary`}
														/>
														<div className="flex h-9 items-center justify-start gap-3 rounded-lg border border-gray-200 bg-white px-2">
															<label className="flex items-center gap-1 cursor-pointer">
																<input
																	type="radio"
																	name={`unconsumed-dose-status-${rxKey}`}
																	checked={unconsumedEntry?.safetyStatus === 'safe'}
																	onChange={() => handleUnconsumedSafetyStatusChange('safe')}
																	className="h-4 w-4 border-gray-300 text-[#10b981] focus:ring-[#10b981]"
																/>
																<span className="text-xs text-gray-800">Un-tampered</span>
															</label>

															<label className="flex items-center gap-1 cursor-pointer">
																<input
																	type="radio"
																	name={`unconsumed-dose-status-${rxKey}`}
																	checked={unconsumedEntry?.safetyStatus === 'unsafe'}
																	onChange={() => handleUnconsumedSafetyStatusChange('unsafe')}
																	className="h-4 w-4 border-gray-300 text-[#10b981] focus:ring-[#10b981]"
																/>
																<span className="text-xs text-gray-800">Tampered</span>
															</label>
														</div>
													</div>

													{unconsumedEntryErrors?.quantity ? (
														<p className="text-xs text-red-600">{unconsumedEntryErrors.quantity}</p>
													) : null}

													{unconsumedEntryErrors?.safetyStatus ? (
														<p className="text-xs text-red-600">{unconsumedEntryErrors.safetyStatus}</p>
													) : null}
												</div>
											)}
										</div>
									)}

									{!rxCanDeliver && !isFullyDelivered && (
										<p className="mt-2 w-full min-w-0 rounded-md bg-slate-700 px-2 py-2 text-[11px] font-medium leading-tight text-white whitespace-normal" style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}>
											{assignedOwnerName ? `Assigned to ${assignedOwnerName}` : 'Assigned to another user'}
										</p>
									)}

									{/* Copay Payment Status Component */}
									{SHOW_DELIVERY_EXTRAS && flexiType && value?.delivery_status === 'pending' && !blisterPackKey && !isOtherRx && (
										<CopayPaymentStatus
											rxData={value}
											index={index}
											rxnumber={value?.rxnumber}
											copayValue={value?.copay}
											winrxDataId={value?.id}
											onPaymentChange={handleCopayPaymentChange}
										/>
									)}

									{/* Flexi Proof Upload - Show only if flexiType exists */}
									{SHOW_DELIVERY_EXTRAS && flexiType && value?.delivery_status === 'pending' && !blisterPackKey && !isOtherRx && (
										<FlexiProofUpload
											rxData={value}
											index={index}
											rxnumber={value?.rxnumber}
											rxId={value?.id}
											proofImage={flexiProofImages[value?.rxnumber]?.file || flexiProofImages[value?.rxnumber] || null}
											setProofImage={(file) => {
												setFlexiProofImages((prev) => {
													if (file === null) {
														// Remove the key when file is null
														const updated = { ...prev };
														delete updated[value?.rxnumber];
														return updated;
													}
													return {
														...prev,
														[value?.rxnumber]: {
															id: value?.id,
															file: file
														}
													};
												});
											}}
										/>
									)}

								</div>
							</div>
						),
						after: (<React.Fragment>
							{isBlisterPackEnd && (
								<div className="mb-3 h-2 rounded-b-md border border-t-0 border-teal-600 bg-teal-50" />
							)}
							{SHOW_DELIVERY_EXTRAS && isCombinedFooter && combinedRxs.length > 0 && (
								<div className="mb-3 rounded-md border border-gray-200 bg-white p-2">
									{/* One Collect for everything ticked: the pack and the other drugs' copays added up. */}
									<CopayPaymentStatus
										rxData={value}
										index={index}
										rxnumber={`all-${patientData?.id ?? ''}`}
										copayValue={combinedCopay}
										winrxDataId={`all-${patientData?.id ?? ''}`}
										onPaymentChange={(_, paymentData) => handleBlisterPackPaymentChange(combinedRxs, paymentData)}
									/>
									{/* One proof image for all of them. */}
									<FlexiProofUpload
										rxData={value}
										index={index}
										rxnumber={`all-${patientData?.id ?? ''}`}
										rxId={value?.id}
										proofImage={flexiProofImages[combinedRxs.find((rx) => rx?.id != null)?.rxnumber]?.file || null}
										setProofImage={(file) => setBlisterPackProofImage(combinedRxs, file)}
									/>
								</div>
							)}
						</React.Fragment>),
					}
				}))
			}

			{/* Vital Signs Entry - Show if patientData is provided and has an id */}
			{patientData?.add_vital ? null :
				(isAllowedPage && patientData?.id && (patientData?.is_vital === 1 &&
					((driverID === patientData?.driver_hcp_id) || !patientData?.driver_hcp_id))) && (
					<VitalSignsEntry
						patientId={patientData?.id}
						onSuccess={handleVitalsSuccess}
						// onRemovePatient={(patientId) => dispatch(removeVitalPatientById(patientId))}
						isAddVitalsPage={isAddVitalsPage}
					/>
				)}
		</React.Fragment>
	)
}
